import React, { useCallback, useEffect } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Form, Input, Row, Col, Button, Popconfirm } from "antd";
import { showJiraWidget, validateEmailAddress } from "@/utils/utils";
import { Validator } from "@/utils/validator";
import { createDealer, updateDealer } from "@/services/nissanDealers";
import AddressInput from "@/components/AddressInput";
import PhoneNumberInput, { flatPhoneNumber } from "@/components/PhoneNumberInput";

export default function NissanDealerForm({
  formRef,
  initialValues,
  editMode = false,
  setLoading,
  refreshTable,
  setVisible,
}) {
  useEffect(() => {
    formRef?.resetFields();
    formRef?.setFieldsValue(initialValues);
  }, [initialValues, formRef]);

  const onSubmit = useCallback(
    values => {
      setLoading(true);

      const apiCaller = editMode ? updateDealer : createDealer;
      const payload = {
        ...values,
        phoneNumber: flatPhoneNumber(values.phoneNumber),
        zipCode: values?.zipCode?.toString(),
        emails: values?.emails?.join(";"),
      };

      apiCaller(payload)
        .then(() => {
          formRef?.resetFields();
          setVisible(false);
          if (typeof refreshTable === "function") {
            refreshTable();
          }

          showJiraWidget();
        })
        .catch(error => {
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [refreshTable, editMode, setVisible, setLoading, formRef],
  );

  return (
    <Form form={formRef} layout="vertical" initialValues={initialValues} onFinish={onSubmit}>
      <Row gutter={16}>
        <Col lg={8}>
          <Form.Item name="code" label="Code" rules={[Validator.required("Please input code")]}>
            <Input />
          </Form.Item>
        </Col>
        <Col lg={8}>
          <Form.Item name="name" label="Name" rules={[Validator.required("Please input name")]}>
            <Input />
          </Form.Item>
        </Col>
        <Col lg={8}>
          <Form.Item name="manager" label="Exec Manager" rules={[Validator.required("Please input manager")]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col lg={12}>
          <PhoneNumberInput
            formItemProps={{
              name: "phoneNumber",
              label: "Phone Number",
              extensionName: "PhoneNumberExtension",
              formRef,
              rules: [Validator.required("Please input phone number")],
              validateTrigger: "onBlur",
            }}
          />
        </Col>
        <Col lg={12}>
          <Form.Item name="fax" label="Fax" rules={[Validator.required("Please input fax")]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col lg={24}>
          <AddressInput
            formReference={{ current: formRef }}
            stateNameFormItemProps={{
              name: "state",
            }}
            stateShortNameFormItemProps={{
              name: "stateShortName",
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col lg={24}>
          <Form.Item name="region" label="Region" rules={[Validator.required("Please input region")]}>
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col flex={1}>
          <Form.Item label="Delivery Email Recipients">
            <Form.List
              name="emails"
              validateTrigger="onBlur"
              rules={[
                {
                  validator: async (_, emails) => {
                    if (!emails || emails.length === 0) {
                      return Promise.resolve();
                    }

                    const oneOrMoreEmailsNotEntered = emails.some(e => !e);

                    if (oneOrMoreEmailsNotEntered) {
                      return Promise.resolve();
                    }

                    const duplicateEmailsEntered = new Set(emails.map(e => e?.toLowerCase())).size !== emails.length;

                    if (duplicateEmailsEntered) {
                      return Promise.reject("One or more duplicate emails entered.");
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map(field => (
                    <Form.Item required={false} key={field.key}>
                      <Form.Item
                        {...field}
                        validateTrigger="onBlur"
                        noStyle
                        rules={[
                          {
                            required: true,
                            validator: (_, value) => {
                              if (!value) {
                                return Promise.reject("Please enter recipient's email.");
                              }

                              if (!validateEmailAddress(value)) {
                                return Promise.reject("Please enter a valid email address.");
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          placeholder="Enter email"
                          style={{
                            width: "60%",
                          }}
                        />
                      </Form.Item>
                      <Popconfirm
                        placement="right"
                        title="Are you sure you want to remove this recipient?"
                        onConfirm={() => remove(field.name)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button type="danger" style={{ marginLeft: 16 }} icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Form.Item>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      style={{
                        width: "60%",
                        marginBottom: 16,
                      }}
                      icon={<PlusOutlined />}
                    >
                      Add new recipient
                    </Button>
                    <Form.ErrorList errors={errors} />
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="id" hidden>
        <Input />
      </Form.Item>
    </Form>
  );
}
